import React, { useMemo, useState, useContext, useEffect, useRef } from 'react';
import {
	Box, Button, Chip, Collapse, Divider, IconButton,
	InputAdornment, LinearProgress, MenuItem, Paper, Stack,
	Table, TableBody, TableCell, TableContainer, TableHead,
	TableRow, TextField, Tooltip, Typography, Autocomplete,
} from '@mui/material';
import DateRangeIcon from '@mui/icons-material/DateRange';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useLocation } from 'react-router-dom';
import serviceName from '../Helpers/SheetHelpers/ServiceNameDisplay';
import serviceIcon from '../Helpers/SheetHelpers/IconFinder';
import dayjs from 'dayjs';
import useWorkHistoryStore, { WHLocationFilter } from '../Stores/WorkHistoryStore';
import UserContext from '../User/UserSettings/UserContext';
import {
	generateServiceSheetPdf,
	generateRefrigerationPdf,
	generatePreSeasonPdf,
	generateSalesOrderPdf,
	generateInspectionPdf,
	generateChemAppsAgreementPdf
} from './Pdf/sheets/components/PdfGeneratorButton';

// Local sheet type options referencing serviceName/serviceIcon. Could later be centralized.
const SHEET_TYPE_OPTIONS = [
	'sheets', 'chemAppService', 'preseason', 'refrigeration', 'salesOrders',
	'chemAppAgreement', 'disinfectantAgreement', 'sprayBarAgreement', 'inspections',
	'chemAppsInspections', 'freshPack'
]; 

type WorkHistoryNavigationState = {
	sheetType?: string | null;
	sheetTypes?: string[] | null;
	sheetId?: string | number | null;
	id?: string | number | null;
	customers?: string[];
	locations?: WHLocationFilter[];
};

const WorkHistoryBase: React.FC = () => {
	const {
		filters, loading, results, selected,
		setSheetTypes, setDateFrom, setDateTo,
		setCustomers, setLocations, setSheetId,
		clearAdvanced, runSearch, setSelected
	} = useWorkHistoryStore();
	const { user } = useContext(UserContext) || {};
	const [showAdvanced, setShowAdvanced] = useState(false);
	const location = useLocation();
	const presetAppliedRef = useRef<string | null>(null);

	useEffect(() => {
		const state = (location.state ?? {}) as WorkHistoryNavigationState;
		const params = new URLSearchParams(location.search ?? '');

		const stateSheetTypes = state.sheetTypes ?? (state.sheetType ? [state.sheetType] : null);
		const stateSheetId = state.sheetId ?? state.id;
		const paramSheetType = params.get('sheetType');
		const paramSheetId = params.get('sheetId');

		const derivedSheetTypes = stateSheetTypes ?? (paramSheetType ? [paramSheetType] : null);
		const derivedSheetId = stateSheetId ?? paramSheetId ?? null;

		if (!derivedSheetTypes && !derivedSheetId) {
			return;
		}

		const signature = `${(derivedSheetTypes ?? []).join(',')}|${derivedSheetId ?? ''}`;
		if (presetAppliedRef.current === signature) {
			return;
		}
		presetAppliedRef.current = signature;

		if (derivedSheetTypes) {
			setSheetTypes(derivedSheetTypes.filter(Boolean) as string[]);
		}

		if (derivedSheetId !== null && derivedSheetId !== undefined) {
			setSheetId(derivedSheetId === '' ? null : String(derivedSheetId));
		}

		if (state.customers?.length) {
			setCustomers(state.customers);
		}

		if (state.locations?.length) {
			setLocations(state.locations);
		}

		runSearch({
			customers: state.customers?.length ? state.customers : user?.customers,
			locations: state.locations?.length ? state.locations : user?.locations
		});
	}, [location.key, location.search, location.state, runSearch, setSheetId, setSheetTypes, setCustomers, setLocations, user?.customers, user?.locations]);

	const canSearch = useMemo(() => {
		if (filters.sheetId) return filters.sheetTypes.length > 0; // require sheet type with ID per requirements
		return filters.sheetTypes.length > 0;
	}, [filters.sheetTypes, filters.sheetId]);

	const onSubmit = () => {
		if (canSearch) runSearch({ customers: user.customers, locations: user.locations });
	};

	const onDownloadPdf = async (rec: any) => {

		try {
			switch (rec.sheetType) {
				case 'sheets':
				case 'chemAppService':
					await generateServiceSheetPdf(rec.sheetType, rec.data, user);
					break;
				case 'refrigeration':
					await generateRefrigerationPdf(rec.sheetType, rec.data, user);
					break;
				case 'preseason':
					await generatePreSeasonPdf(rec.sheetType, rec.data, user);
					break;
				case 'salesOrders':
					await generateSalesOrderPdf(rec.sheetType, rec.data, user);
					break;
				case 'inspections':
				case 'chemAppsInspections':
					await generateInspectionPdf(rec.sheetType, rec.data, user);
					break;
				case 'chemAppAgreement':
				case 'disinfectantAgreement':
				case 'sprayBarAgreement':
					await generateChemAppsAgreementPdf(rec.sheetType, rec.data, user);
					break;
				default:
					alert(`PDF generation for ${serviceName(rec.sheetType)} is not yet implemented.`);
					return;
			}
		} catch (error) {
			console.error('[WorkHistory] Failed to generate PDF:', error);
			alert('Failed to generate PDF. Please try again.');
		}
	};

	return (
		<Box p={2}>
			<Typography variant="h5" gutterBottom>Work History</Typography>
			<Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
				<Box
					display="grid"
					alignItems="flex-end"
					gap={2}
					sx={{ gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(6, 1fr)' } }}
				>
					<Box sx={{ gridColumn: { xs: '1 / -1', md: 'span 2' } }}>
						<TextField
							select
							fullWidth
							label="Sheet Types"
							SelectProps={{
								multiple: true,
								renderValue: (vals) => {
									const selected = vals as string[];
									if (!selected.length) return 'None';
									if (selected.length <= 3) {
										return (
											<Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.5, overflow: 'hidden' }}>
												{selected.map(v => (
													<Chip key={v} size="small" label={serviceName(v)} icon={<Box component="span">{serviceIcon(v)}</Box>} />
												))}
											</Box>
										);
									}
									return (
										<Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.5, overflow: 'hidden', alignItems: 'center' }}>
											{selected.slice(0, 2).map(v => (
												<Chip key={v} size="small" label={serviceName(v)} icon={<Box component="span">{serviceIcon(v)}</Box>} />
											))}
											<Chip size="small" label={`+${selected.length - 2} more`} />
										</Box>
									);
								}
							}}
							sx={{
								'& .MuiSelect-select': {
									display: 'flex',
									alignItems: 'center',
									gap: 4,
									minHeight: '48px',
									py: 0.5
								}
							}}
							value={filters.sheetTypes}
							onChange={e => setSheetTypes(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value as string[])}
							helperText="Select one or more sheet types"
						>
							{SHEET_TYPE_OPTIONS.map((k: any) => (
								<MenuItem key={k} value={k}>{serviceIcon(k)}<Box ml={1}>{serviceName(k)}</Box></MenuItem>
							))}
						</TextField>
					</Box>
					<Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 1', md: 'span 1' }, mb: 2.75 }}>
						<TextField
							type="date"
							fullWidth
							label="From"
							InputLabelProps={{ shrink: true }}
							value={filters.dateFrom ? filters.dateFrom.format('YYYY-MM-DD') : ''}
							onChange={(e) => setDateFrom(e.target.value ? dayjs(e.target.value) : null)}
							InputProps={{ startAdornment: <InputAdornment position="start"><DateRangeIcon fontSize="small" /></InputAdornment> }}
						/>
					</Box>
					<Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 1', md: 'span 1' }, mb: 2.75 }}>
						<TextField
							type="date"
							fullWidth
							label="To"
							InputLabelProps={{ shrink: true }}
							value={filters.dateTo ? filters.dateTo.format('YYYY-MM-DD') : ''}
							onChange={(e) => setDateTo(e.target.value ? dayjs(e.target.value) : null)}
							InputProps={{ startAdornment: <InputAdornment position="start"><DateRangeIcon fontSize="small" /></InputAdornment> }}
						/>
					</Box>
					<Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 1', md: 'span 1' }, mb: 2.75 }}>
						<TextField
							fullWidth
							label="Sheet ID (optional)"
							value={filters.sheetId || ''}
							onChange={(e) => setSheetId(e.target.value || null)}
						/>
					</Box>
					<Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 2', md: 'span 1' } }}>
						<Stack direction="row" spacing={1} justifyContent="flex-start" alignItems="center" sx={{ mt: { xs: 1, md: 0 } }}>
							<Tooltip title={showAdvanced ? 'Hide advanced filters' : 'Show advanced filters'}>
								<IconButton onClick={() => setShowAdvanced(v => !v)} color={showAdvanced ? 'primary' : 'default'}>
									{showAdvanced ? <FilterAltOffIcon /> : <FilterAltIcon />}
								</IconButton>
							</Tooltip>
							<Tooltip title="Reset advanced filters">
								<IconButton onClick={clearAdvanced}><RestartAltIcon /></IconButton>
							</Tooltip>
							<Button variant="contained" startIcon={<SearchIcon />} disabled={!canSearch || loading} onClick={onSubmit}>Search</Button>
						</Stack>
					</Box>
				</Box>
				<Collapse in={showAdvanced} timeout="auto" unmountOnExit>
					<Divider sx={{ my: 2 }} />
					<Box display="grid" gap={2} sx={{ gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
						<Box>
							<Autocomplete
								multiple
								options={user.customers || []}
								value={filters.customers}
								onChange={(_, val) => setCustomers(val)}
								renderTags={(value, getTagProps) => value.map((option, index) => (
									<Chip {...getTagProps({ index })} key={option} label={option} />
								))}
								renderInput={(params) => <TextField {...params} label="Customers" placeholder="Select customers" />}
							/>
						</Box>
						<Box>
							<Autocomplete
								multiple
								options={user.locations}
								getOptionLabel={(o) => o.locationName || ''}
								isOptionEqualToValue={(o, v) => o.id === v.id}
								value={filters.locations}
								onChange={(_, val) => setLocations(val)}
								renderTags={(value, getTagProps) => value.map((option, index) => (
									<Chip {...getTagProps({ index })} key={option.id} label={option.locationName} />
								))}
								renderInput={(params) => <TextField {...params} label="Storages" placeholder="Select storages" />}
							/>
						</Box>
						<Box>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Select customers / storages or leave blank for all available.</Typography>
						</Box>
					</Box>
				</Collapse>
				{loading && <LinearProgress sx={{ mt: 2 }} />}
			</Paper>

			<Paper variant="outlined" sx={{ p: 2 }}>
				<Typography variant="subtitle1" gutterBottom>Results ({results.length})</Typography>
				<TableContainer sx={{ maxHeight: 560 }}>
					<Table size="small" stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell>Type</TableCell>
								<TableCell>Date</TableCell>
								<TableCell>Storage</TableCell>
								<TableCell>Summary</TableCell>
								<TableCell align="right">Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{results.map(r => {
								const active = selected?.id === r.id;
								return (
									<TableRow hover key={`${r.sheetType}-${r.id}`} selected={active} onClick={() => setSelected(r)} sx={{ cursor: 'pointer' }}>
										<TableCell width={160}>
											<Stack direction="row" spacing={1} alignItems="center">
												{serviceIcon(r.sheetType)}
												<Typography variant="body2" fontWeight={500}>{serviceName(r.sheetType)}</Typography>
											</Stack>
										</TableCell>
										<TableCell width={140}>{r.serviceDate ? dayjs(r.serviceDate).format('YYYY-MM-DD') : '-'}</TableCell>
										<TableCell width={220}>{r.locationName || '-'}</TableCell>
										<TableCell>{r.summary || <Typography variant="caption" color="text.secondary">(no summary)</Typography>}</TableCell>
										<TableCell align="right" width={120}>
											<Tooltip title="Download PDF (logs data)">
												<IconButton size="small" onClick={(e) => { e.stopPropagation(); onDownloadPdf(r); }}>
													<PictureAsPdfIcon fontSize="small" />
												</IconButton>
											</Tooltip>
										</TableCell>
									</TableRow>
								);
							})}
							{(!loading && results.length === 0) && (
								<TableRow>
									<TableCell colSpan={5}>
										<Typography variant="body2" color="text.secondary">No results yet. Adjust filters and search.</Typography>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</Paper>
		</Box>
	);
};

export default WorkHistoryBase